import type { ColDef, ColGroupDef } from 'ag-grid-community'
import type { DagColumnMeta, DropdownOptionRead } from '@/types/dag'
import { isCellEditable } from './DagCellRenderer'
import { getQuantityShortfallTooltip } from './quantityShortfall'
import { DagConditionalHeader } from './DagConditionalHeader'
import { DagBulkEditColumnHeader } from './DagBulkEditColumnHeader'
import {
  DagColumnHeaderLabel,
  type DagColumnHeaderLabelProps,
} from './DagColumnHeaderLabel'
import {
  DagColumnHeaderWithTotals,
  type ColumnTotal,
} from './DagColumnHeaderWithTotals'
import { HIDDEN_ATTRIBUTE_IDS } from './columnConfiguration'
import { defaultColumnWidth } from './headerWidth'
import type { RowData } from './useProductsTableState'

// Columns handled by fixed renderers — skip from dynamic generation
const FIXED_COLUMN_IDS = new Set(['description', 'plan_sku_num'])

// Fixed/pinned columns — never draggable, order is not user-configurable
export const NON_REORDERABLE_COLUMN_IDS = new Set([
  'description',
  'plan_sku_num',
  'actions',
])

// The only column the "bulk edit" pencil icon appears on (Total Sales Units)
export const BULK_EDIT_COLUMN_ID = 'total_sales_forecast'

const PIAB_QTY_CONDITION_DESCRIPTION =
  'PIAB Qty is only editable when SKU Level is set to Parent.'

// Plain-text explanations for conditional columns whose backend metadata
// has no editable_condition_description (otherwise the raw formula shows)
const CONDITION_DESCRIPTIONS: Record<string, string> = {
  piab_purchase_qty: PIAB_QTY_CONDITION_DESCRIPTION,
  is_piab_qty_manual: PIAB_QTY_CONDITION_DESCRIPTION,
}

// Conditional columns that render greyed out (like read-only cells)
// instead of darker blue when the condition isn't met
const GREY_WHEN_LOCKED_COLUMN_IDS = new Set([
  'piab_purchase_qty',
  'is_piab_qty_manual',
])

/**
 * Resolves the group order and per-group column order from a flat, saved
 * column order (as stored by /plans/{id}/column-order or a saved view's
 * columns). A column's position in `savedOrder` determines both where
 * its group sorts relative to other groups (by the group's earliest member)
 * and where it sorts within its own group — this is how a single flat list
 * encodes both "reorder groups as blocks" and "reorder within a group".
 * Columns missing from `savedOrder` (e.g. newly added DAG columns) fall
 * back to their original metadata order, appended after known columns.
 */
export function resolveColumnOrder(
  columns: DagColumnMeta[],
  groups: string[],
  savedOrder?: string[]
): { orderedGroups: string[]; columnsByGroup: Map<string, DagColumnMeta[]> } {
  const savedIndex = new Map<string, number>()
  savedOrder?.forEach((id, i) => savedIndex.set(id, i))

  const fallbackBase = savedOrder?.length ?? 0
  const resolvedIndex = new Map<string, number>()
  columns.forEach((col, i) => {
    resolvedIndex.set(col.id, savedIndex.get(col.id) ?? fallbackBase + i)
  })

  const columnsByGroup = new Map<string, DagColumnMeta[]>()
  for (const col of columns) {
    const groupCols = columnsByGroup.get(col.group) ?? []
    groupCols.push(col)
    columnsByGroup.set(col.group, groupCols)
  }
  for (const groupCols of columnsByGroup.values()) {
    groupCols.sort(
      (a, b) => resolvedIndex.get(a.id)! - resolvedIndex.get(b.id)!
    )
  }

  const groupMinIndex = new Map<string, number>()
  for (const groupName of groups) {
    const groupCols = columnsByGroup.get(groupName)
    if (!groupCols || groupCols.length === 0) continue
    groupMinIndex.set(
      groupName,
      Math.min(...groupCols.map(c => resolvedIndex.get(c.id)!))
    )
  }

  const orderedGroups = groups
    .filter(g => groupMinIndex.has(g))
    .sort((a, b) => groupMinIndex.get(a)! - groupMinIndex.get(b)!)

  return { orderedGroups, columnsByGroup }
}

export interface BuildDagColumnDefsOptions {
  columns: DagColumnMeta[]
  groups: string[]
  columnOrder?: string[]
  columnWidths?: Record<string, number>
  dropdownMap: Record<string, DropdownOptionRead[]>
  hiddenColumnIds?: Set<string>
  isReadOnly?: boolean
  showActions?: boolean
  columnTotals?: Record<string, ColumnTotal[]>
  onOpenBulkEditColumn?: () => void
  // Fixed column components (provided by ProductsTable)
  NameHeaderComponent: React.ComponentType<DagColumnHeaderLabelProps>
  NameCellRenderer: React.ComponentType<{ data?: RowData }>
  SkuCellRenderer: (props: { data?: RowData }) => React.ReactNode
  ActionsCellRenderer: (props: { data?: RowData }) => React.ReactNode
  DagCellRendererComponent: React.ComponentType<{
    data?: RowData
    colDef?: { colId?: string }
    columnMeta: DagColumnMeta
    dropdownOptions?: DropdownOptionRead[]
  }>
}

export function buildDagColumnDefs(
  opts: BuildDagColumnDefsOptions
): (ColDef<RowData> | ColGroupDef<RowData>)[] {
  const {
    columns,
    groups,
    columnOrder,
    columnWidths,
    dropdownMap,
    hiddenColumnIds,
    columnTotals,
    NameHeaderComponent,
    NameCellRenderer,
    SkuCellRenderer,
    ActionsCellRenderer,
    DagCellRendererComponent,
  } = opts

  // 1. Build the fixed "Item" group with Description + SKU (metadata-driven)
  const descMeta = columns.find(c => c.id === 'description')
  const skuMeta = columns.find(c => c.id === 'plan_sku_num')
  const skuTotals = columnTotals?.['plan_sku_num']

  const itemGroup: ColGroupDef<RowData> = {
    headerName: 'Item',
    headerClass: 'ag-group-header-center',
    suppressStickyLabel: true,
    children: [
      {
        colId: 'description',
        headerName: descMeta?.label ?? 'Description',
        headerComponent: NameHeaderComponent,
        headerComponentParams: {
          isRequiredInput: descMeta?.is_required_input === true,
        },
        initialWidth:
          columnWidths?.['description'] ??
          defaultColumnWidth(
            descMeta?.width || 400,
            descMeta?.label ?? 'Description',
            { isRequiredInput: descMeta?.is_required_input === true }
          ),
        pinned: 'left',
        lockPinned: true,
        suppressMovable: true,
        rowDrag: params => !opts.isReadOnly && !params.data?.isOptionRow,
        cellRenderer: NameCellRenderer,
        cellClassRules: {
          'dag-cell-readonly': params => {
            if (!params.data || params.data.isOptionRow) return false
            if (opts.isReadOnly) return true
            const cells = params.data.cells ?? {}
            const sku = cells['plan_sku_num']
            const isNewSku = !!cells['is_new_sku']
            return !!sku && !isNewSku
          },
        },
        sortable: true,
        resizable: true,
        filter: false,
        suppressKeyboardEvent: () => true,
        valueGetter: params => params.data?.cells?.['description'] ?? '',
      },
      {
        colId: 'plan_sku_num',
        headerName: skuMeta?.label ?? 'SKU #',
        headerComponentParams: {
          innerHeaderComponent: DagColumnHeaderLabel,
          innerHeaderComponentParams: {
            isRequiredInput: skuMeta?.is_required_input === true,
          },
        },
        initialWidth:
          columnWidths?.['plan_sku_num'] ??
          defaultColumnWidth(skuMeta?.width || 130, skuMeta?.label ?? 'SKU #', {
            isRequiredInput: skuMeta?.is_required_input === true,
          }),
        pinned: 'left',
        lockPinned: true,
        suppressMovable: true,
        cellRenderer: SkuCellRenderer,
        resizable: true,
        filter: false,
        suppressKeyboardEvent: () => true,
        valueGetter: params => params.data?.cells?.['plan_sku_num'] ?? '',
        ...(skuTotals && skuTotals.length > 0
          ? {
              headerComponent: DagColumnHeaderWithTotals,
              headerComponentParams: {
                totals: skuTotals,
                isRequiredInput: skuMeta?.is_required_input === true,
              },
            }
          : {}),
      },
    ],
  }

  // 2. Build dynamic column groups from DAG metadata
  const dynamicColumns = columns.filter(
    c =>
      c.kind !== 'aggregate' &&
      !FIXED_COLUMN_IDS.has(c.id) &&
      !HIDDEN_ATTRIBUTE_IDS.has(c.id) &&
      !hiddenColumnIds?.has(c.id)
  )

  // Resolve group order + per-group column order from the saved/persisted
  // flat column order (falls back to metadata array order for unknown ids)
  const { orderedGroups, columnsByGroup } = resolveColumnOrder(
    dynamicColumns,
    groups,
    columnOrder
  )

  // Build grouped column defs in the resolved order
  const dynamicGroups: ColGroupDef<RowData>[] = []

  for (const groupName of orderedGroups) {
    const groupCols = columnsByGroup.get(groupName)
    if (!groupCols || groupCols.length === 0) continue

    // Skip the "Identity" group as it's handled by fixed columns
    if (groupName === 'Identity') continue

    const children: ColDef<RowData>[] = groupCols.map(meta => {
      const isRequiredInput = meta.is_required_input === true
      const colDef: ColDef<RowData> = {
        colId: meta.id,
        headerName: meta.label,
        headerComponentParams: {
          innerHeaderComponent: DagColumnHeaderLabel,
          innerHeaderComponentParams: { isRequiredInput, isReorderable: true },
        },
        initialWidth:
          columnWidths?.[meta.id] ??
          defaultColumnWidth(meta.width || 150, meta.label, {
            isReorderable: true,
            isRequiredInput,
            // Bulk edit header adds a pencil icon (h-3 w-3 + gap-1)
            extraIconPx: meta.id === BULK_EDIT_COLUMN_ID ? 16 : 0,
          }),
        cellRenderer: DagCellRendererComponent,
        cellRendererParams: {
          columnMeta: meta,
          dropdownOptions: meta.dropdown_category
            ? (dropdownMap[meta.dropdown_category] ??
              meta.options?.map(o => ({ value: o })))
            : undefined,
        },
        cellClassRules: {
          // Light blue: conditionally editable AND currently editable
          'dag-cell-conditional': params => {
            if (!params.data || params.data.isOptionRow) return false
            if (opts.isReadOnly) return false
            // num_stores is editable when no store model — highlight with light blue
            if (
              meta.id === 'num_stores' &&
              !params.data.cells?.['store_model_id']
            ) {
              return true
            }
            if (meta.editable_when !== 'conditional') return false
            return isCellEditable(meta, params.data.cells ?? {})
          },
          // Darker blue: conditionally editable but currently NOT editable
          'dag-cell-conditional-locked': params => {
            if (!params.data || params.data.isOptionRow) return false
            if (opts.isReadOnly) return false
            if (meta.editable_when !== 'conditional') return false
            if (GREY_WHEN_LOCKED_COLUMN_IDS.has(meta.id)) return false
            return !isCellEditable(meta, params.data.cells ?? {})
          },
          'dag-cell-readonly': params => {
            if (!params.data || params.data.isOptionRow) return false
            if (opts.isReadOnly) return true
            // Conditional cells get blue instead of gray (unless opted out)
            if (
              meta.editable_when === 'conditional' &&
              !GREY_WHEN_LOCKED_COLUMN_IDS.has(meta.id)
            ) {
              return false
            }
            return !isCellEditable(meta, params.data.cells ?? {})
          },
          'dag-cell-warning': params =>
            Boolean(
              getQuantityShortfallTooltip(
                meta.id,
                params.data?.cells,
                params.data?.isOptionRow
              )
            ),
        },
        suppressKeyboardEvent: () => true,
        sortable: false,
        resizable: true,
        filter: false,
      }

      const totalsForCol = columnTotals?.[meta.id]
      if (meta.id === BULK_EDIT_COLUMN_ID && opts.onOpenBulkEditColumn) {
        colDef.headerComponent = DagBulkEditColumnHeader
        colDef.headerComponentParams = {
          onOpenBulkEditColumn: opts.onOpenBulkEditColumn,
          isRequiredInput,
          isReorderable: true,
        }
      } else if (totalsForCol && totalsForCol.length > 0) {
        colDef.headerComponent = DagColumnHeaderWithTotals
        colDef.headerComponentParams = {
          totals: totalsForCol,
          isRequiredInput,
          isReorderable: true,
        }
      } else if (meta.editable_when === 'conditional') {
        const tooltipText =
          meta.editable_condition_description ??
          CONDITION_DESCRIPTIONS[meta.id] ??
          meta.editable_condition
        if (tooltipText) {
          colDef.headerComponent = DagConditionalHeader
          colDef.headerComponentParams = {
            tooltipText,
            isRequiredInput,
            isReorderable: true,
          }
        }
      }

      return colDef
    })

    dynamicGroups.push({
      headerName: groupName,
      headerClass: 'ag-group-header-center',
      children,
    })
  }

  // 3. Fixed right-pinned column: Actions (optional)
  if (opts.showActions !== false) {
    const actionsCol: ColDef<RowData> = {
      colId: 'actions',
      headerName: 'Actions',
      pinned: 'right',
      suppressMovable: true,
      cellRenderer: ActionsCellRenderer,
      headerClass: 'ag-pinned-header',
      sortable: false,
    }
    return [itemGroup, ...dynamicGroups, actionsCol]
  }

  return [itemGroup, ...dynamicGroups]
}

/**
 * Flattens a columnDefs tree (groups + leaf columns) into an ordered list of
 * colIds, in the same order the defs were built. Used to force AG Grid to
 * re-sort its columns to match the resolved order — `maintainColumnOrder`
 * keeps the grid's existing column order across columnDefs updates, so
 * switching between saved views (or the plan's persisted order) otherwise
 * has no visible effect when the set of visible columns is unchanged.
 */
export function flattenColumnIds(
  defs: (ColDef<RowData> | ColGroupDef<RowData>)[]
): string[] {
  const ids: string[] = []
  for (const def of defs) {
    if ('children' in def && def.children) {
      ids.push(...flattenColumnIds(def.children))
    } else if ('colId' in def && def.colId) {
      ids.push(def.colId)
    }
  }
  return ids
}
